/**
 * CreateFolderModal Component
 *
 * Modal for creating or editing workflow folders.
 * Chrome via Modal shared (F71/F72 — tema claro fixo + bottom-sheet mobile de graça).
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Input, Textarea } from '@/shared/components/ui';
import type { WorkflowFolderResponse, CreateWorkflowFolderRequest } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateWorkflowFolderRequest) => Promise<void>;
  folder?: WorkflowFolderResponse | null;
  parentId?: string | null;
}

const FOLDER_COLORS = [
  '#8b5cf6', // Purple (default)
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
  '#ec4899', // Pink
  '#6b7280', // Gray
];

/**
 * CreateFolderModal Component
 *
 * Modal for creating/editing folders with name, description, and color.
 */
export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  folder,
  parentId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(FOLDER_COLORS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!folder;

  // Reset form when modal opens/closes or folder changes
  useEffect(() => {
    if (isOpen) {
      if (folder) {
        setName(folder.name);
        setDescription(folder.description || '');
        setColor(folder.color || FOLDER_COLORS[0]);
      } else {
        setName('');
        setDescription('');
        setColor(FOLDER_COLORS[0]);
      }
      setError(null);
    }
  }, [isOpen, folder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('O nome da pasta é obrigatório');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        parentId: parentId || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar pasta');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <div data-testid="create-folder-modal">
        {/* Header (Modal shared) */}
        <ModalHeader onClose={onClose}>
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${color}20` }}
            >
              <Icon name="folder" className="w-5 h-5" style={{ color }} />
            </div>
            <h2
              className="text-lg font-semibold"
              style={{ color: '#1e293b' }}
            >
              {isEditMode ? 'Editar Pasta' : 'Nova Pasta'}
            </h2>
          </div>
        </ModalHeader>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <ModalContent className="space-y-4">
            {/* Error Message */}
            {error && (
              <div
                className="p-3 rounded-lg text-sm"
                style={{
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                }}
              >
                {error}
              </div>
            )}

            {/* Name Field */}
            <div>
              <label
                className="block text-sm font-medium mb-1.5"
                style={{ color: '#1e293b' }}
              >
                Nome *
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome da pasta"
                autoFocus
              />
            </div>

            {/* Description Field */}
            <div>
              <Textarea
                label="Descricao"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descricao opcional da pasta"
                rows={3}
                resize="none"
              />
            </div>

            {/* Color Picker */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: '#1e293b' }}
              >
                Cor
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {FOLDER_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className="w-8 h-8 rounded-full transition-transform hover:scale-110"
                    style={{
                      backgroundColor: c,
                      boxShadow: color === c ? `0 0 0 2px #ffffff, 0 0 0 4px ${c}` : 'none',
                    }}
                    aria-label={`Selecionar cor ${c}`}
                  />
                ))}
              </div>
            </div>
          </ModalContent>

          {/* Footer — F71: Cancelar = Button secondary */}
          <ModalFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
              data-testid="create-folder-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              data-testid="create-folder-submit"
            >
              {isSubmitting ? 'Salvando...' : isEditMode ? 'Salvar' : 'Criar Pasta'}
            </Button>
          </ModalFooter>
        </form>
      </div>
    </Modal>
  );
};

export default CreateFolderModal;
